/**
 * The read-only BPMN thumbnail on the cover of every process-template card.
 *
 * Each template renders ITS OWN diagram (the `xml` the backend catalog serves) into a
 * standalone SVG string, handed out as an already-encoded data URI. Renders are cached
 * PER TEMPLATE at module level and keyed by template id, so a grid of N cards costs at
 * most one bpmn-js Viewer + import + encode per DISTINCT template per session — remounts
 * and tab switches are free, and concurrent first mounts of the same template share one
 * in-flight render instead of racing.
 *
 * Failure is soft on purpose: if bpmn-js cannot import or export a diagram its entry
 * resolves to `undefined` and that card falls back to its empty cover surface. It must
 * never throw into render, and it must not log — a decorative thumbnail is not worth
 * console noise, and the placeholder cover is the signal.
 */

// diagram-js.css is the only stylesheet needed. The exported SVG is self-contained:
// bpmn-js writes geometry, stroke/fill colours and label font styles as inline
// attributes per element (this is bpmn.io's own "download as SVG" path). The bpmn
// icon FONT is used by palettes and menus, not by diagram shapes, so it is not
// imported here.
import "bpmn-js/dist/assets/diagram-js.css";
import type { SaveSVGResult } from "bpmn-js/lib/BaseViewer";
import Viewer from "bpmn-js/lib/Viewer";
import type { ModuleDeclaration } from "didi";
import { useEffect, useState } from "react";
import customModdleExtension from "./moddle/custom.json";
import customWFSharedElementsModule from "./moddle/customWFSharedElementsModule";

/** Offscreen render box — comfortably larger than the templates' widest lane. */
const RENDER_BOX_WIDTH: number = 1600;
const RENDER_BOX_HEIGHT: number = 600;

const previewPromises: Map<string, Promise<string | undefined>> = new Map<string, Promise<string | undefined>>();
const resolvedPreviews: Map<string, string | undefined> = new Map<string, string | undefined>();

const renderPreviewSrc = async (xml: string): Promise<string | undefined> => {
  if (typeof document === "undefined") return undefined;

  const container: HTMLDivElement = document.createElement("div");
  // bpmn-js exports through getBBox(), which reports a 0×0 box for a node that was
  // never laid out — so the container is attached OFFSCREEN rather than left detached
  // or hidden with `display: none`, then removed as soon as the string is captured.
  container.style.cssText = [
    "position: fixed",
    "top: 0",
    `left: -${RENDER_BOX_WIDTH * 2}px`,
    `width: ${RENDER_BOX_WIDTH}px`,
    `height: ${RENDER_BOX_HEIGHT}px`,
    "opacity: 0",
    "pointer-events: none"
  ].join(";");
  document.body.appendChild(container);

  // Same custom moddle + renderer pair the editor's own viewers register (see
  // version-diagram-modal / bpmn.service.createViewer), so a thumbnail draws with the
  // designer's shape styling instead of stock BPMN — the exported SVG carries the custom
  // renderer's inline attributes.
  const viewer: Viewer = new Viewer({
    container,
    moddleExtensions: { custom: customModdleExtension },
    additionalModules: [customWFSharedElementsModule() as unknown as ModuleDeclaration]
  });
  try {
    await viewer.importXML(xml);
    const exported: SaveSVGResult = await viewer.saveSVG();
    // Encoded once per template: cards remounting later reuse the cached src, and
    // identical `src` values share a single decoded image in the browser.
    return `data:image/svg+xml;utf8,${encodeURIComponent(exported.svg)}`;
  } catch {
    return undefined;
  } finally {
    viewer.destroy();
    container.remove();
  }
};

/**
 * Starts the render for this template on first call and hands every later caller the
 * same promise, so N mounts of one template trigger exactly ONE bpmn-js import.
 * Never rejects.
 */
export const getTemplatePreviewSrc = (templateId: string, xml: string): Promise<string | undefined> => {
  let promise: Promise<string | undefined> | undefined = previewPromises.get(templateId);
  if (!promise) {
    promise = renderPreviewSrc(xml).then((src: string | undefined): string | undefined => {
      resolvedPreviews.set(templateId, src);
      return src;
    });
    previewPromises.set(templateId, promise);
  }
  return promise;
};

/**
 * The cover's `<img src>` for one template, or `undefined` while its render is still
 * in flight (or if it failed). Cards mounting after it resolved read it synchronously,
 * so returning to the Templates tab never flashes the placeholder again.
 */
export const useTemplatePreviewSrc = (templateId: string, xml: string): string | undefined => {
  const [src, setSrc] = useState<string | undefined>(resolvedPreviews.get(templateId));

  useEffect((): (() => void) => {
    let isMounted: boolean = true;
    if (!resolvedPreviews.has(templateId)) {
      getTemplatePreviewSrc(templateId, xml).then((resolved: string | undefined): void => {
        if (isMounted) setSrc(resolved);
      });
    } else {
      setSrc(resolvedPreviews.get(templateId));
    }
    return (): void => {
      isMounted = false;
    };
  }, [templateId, xml]);

  return src;
};
